/**
 * Simple tokenizer for Jexl expressions for syntax highlighting.
 * Returns tokens with type: string, number, keyword, operator, punctuation, identifier.
 */
export type JexlTokenType = 'string' | 'number' | 'keyword' | 'operator' | 'punctuation' | 'identifier' | 'space';

export interface JexlToken {
  type: JexlTokenType;
  value: string;
}

const KEYWORDS = new Set(['today', 'true', 'false', 'null']);
const OPERATOR_PATTERN = /^(==|!=|>=|<=|&&|\|\||!|>|<|\+|-|\*|\/|%|\?|:)/;

function tokenizeJexl(input: string): JexlToken[] {
  const tokens: JexlToken[] = [];
  let pos = 0;
  const s = input;

  while (pos < s.length) {
    // String (single or double quoted)
    const singleQuote = s[pos] === "'";
    const doubleQuote = s[pos] === '"';
    if (singleQuote || doubleQuote) {
      const quote = singleQuote ? "'" : '"';
      let end = pos + 1;
      while (end < s.length && s[end] !== quote) {
        if (s[end] === '\\') end += 2;
        else end += 1;
      }
      tokens.push({ type: 'string', value: s.slice(pos, end + 1) });
      pos = end + 1;
      continue;
    }

    // Whitespace
    if (/\s/.test(s[pos])) {
      let end = pos;
      while (end < s.length && /\s/.test(s[end])) end++;
      tokens.push({ type: 'space', value: s.slice(pos, end) });
      pos = end;
      continue;
    }

    // Number
    const numMatch = s.slice(pos).match(/^\d+\.?\d*/);
    if (numMatch) {
      tokens.push({ type: 'number', value: numMatch[0] });
      pos += numMatch[0].length;
      continue;
    }

    // Operator (multi-char first)
    const opMatch = s.slice(pos).match(OPERATOR_PATTERN);
    if (opMatch) {
      tokens.push({ type: 'operator', value: opMatch[0] });
      pos += opMatch[0].length;
      continue;
    }

    // Identifier or keyword (letters, digits, underscore, or bracket access)
    const idMatch = s.slice(pos).match(/^[a-zA-Z_][a-zA-Z0-9_]*/);
    if (idMatch) {
      const value = idMatch[0];
      tokens.push({
        type: KEYWORDS.has(value.toLowerCase()) ? 'keyword' : 'identifier',
        value
      });
      pos += value.length;
      continue;
    }

    // Punctuation: ., () [] []
    if (/[.,()[\]]/.test(s[pos])) {
      tokens.push({ type: 'punctuation', value: s[pos] });
      pos += 1;
      continue;
    }

    // Single char fallback (e.g. = without second =)
    tokens.push({ type: 'punctuation', value: s[pos] });
    pos += 1;
  }

  return tokens;
}

/**
 * Tokenize text for highlighting. If it's a default value in {# ... }, only the inner part is tokenized as Jexl.
 */
export function tokenizeForHighlight(value: string): { tokens: JexlToken[]; isExpression: boolean } {
  const trimmed = value.trim();
  if (!trimmed) {
    return { tokens: [], isExpression: false };
  }
  const isExpression =
    trimmed.startsWith('{#') &&
    trimmed.endsWith('}') &&
    trimmed.length > 3;
  const toTokenize = isExpression
    ? trimmed.slice(2, -1).trim()
    : trimmed;
  const tokens = tokenizeJexl(toTokenize);
  if (isExpression) {
    return {
      tokens: [{ type: 'punctuation', value: '{#' }, ...tokens, { type: 'punctuation', value: '}' }],
      isExpression: true
    };
  }
  return { tokens, isExpression: false };
}

export { tokenizeJexl };

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Token class names for use in HTML highlight (e.g. in code editor). */
const tokenClassNames: Record<JexlTokenType, string> = {
  string: 'jexl-token-string',
  number: 'jexl-token-number',
  keyword: 'jexl-token-keyword',
  operator: 'jexl-token-operator',
  punctuation: 'jexl-token-punctuation',
  identifier: 'jexl-token-identifier',
  space: ''
};

/**
 * Return HTML string with syntax-highlighted spans for use in react-simple-code-editor.
 * For defaultValueStyle, only values in {# ... } are highlighted; otherwise the whole string is Jexl.
 */
export function highlightJexlToHtml(code: string, defaultValueStyle = false): string {
  const { tokens, isExpression } = tokenizeForHighlight(code);
  if (tokens.length === 0) return escapeHtml(code);
  if (defaultValueStyle && !isExpression) return escapeHtml(code);
  return tokens
    .map((t) => {
      const cls = tokenClassNames[t.type];
      const escaped = escapeHtml(t.value);
      return cls ? `<span class="${cls}">${escaped}</span>` : escaped;
    })
    .join('');
}
